// --------------------- Import Globais ---------------------
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// --------------------- Import Antd-Design ---------------------
import { Modal, Form, Input, Select } from "antd";

// --------------------- Import Styles ---------------------
import { formStyle, layout, Title } from "../../ViewGerenciarPostagens/styles";

// --------------------- Import Hooks ---------------------
import { useCadastrarLead } from "../hooks/useCadastrarLead";

// --------------------- Import Uteis ---------------------
import { useError } from "../../../uteis/mensagens/useError";
import { useSuccess } from "../../../uteis/mensagens/useSuccess";
import { useWarning } from "../../../uteis/mensagens/useWarning";

// --------------------- Phone Input ---------------------
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// --------------------- Services ---------------------
import { buscarEtiquetasService } from "../../../services/buscarEtiquetasService";
import { setEtiquetasPessoaService } from "../../../services/setEtiquetasPessoaService"; // <-- se você tiver esse service pronto

type EtiquetaOption = { id: number; nome: string; cor?: string | null };

export default function ModalAdicionar({ isModalOpen, handleFechar }: any) {
  const [form] = Form.useForm();
  const changing = useRef<boolean>(false);

  const {
    cadastrar,
    isLoading: isLoadingCadastrar,
    error,
    success,
    warning,
    resetError,
    resetWarning,
  } = useCadastrarLead();

  const { showError, contextHolder: errorContextHolder } = useError();
  const { showSuccess, contextHolder: successContextHolder } = useSuccess();
  const { showWarning, contextHolder: warningContextHolder } = useWarning();

  const [errorDisplayed, setErrorDisplayed] = useState(false);
  const [successDisplayed, setSuccessDisplayed] = useState(false);
  const [warningDisplayed, setWarningDisplayed] = useState(false);

  const navigate = useNavigate();
  const { TextArea } = Input;

  // --------------------- Estado do form ---------------------
  const [lead, setLead] = useState<any>({
    nome: "",
    celular: "",
    observacoes: "",
    // etiquetasIds: []  // (opcional) se quiser capturar etiquetas aqui
  });

  // --------------------- Etiquetas (opcional) ---------------------
  const [etiquetasOptions, setEtiquetasOptions] = useState<EtiquetaOption[]>([]);
  const [etiquetasSelecionadas, setEtiquetasSelecionadas] = useState<number[]>([]);

  useEffect(() => {
    if (!isModalOpen) return;

    (async () => {
      const data = await buscarEtiquetasService();
      if (Array.isArray(data)) {
        const opts = data
          .filter((e) => e.active === 1)
          .map((e) => ({ id: e.id, nome: e.nome, cor: e.cor ?? null }))
          .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

        setEtiquetasOptions(opts);
      }
    })();
  }, [isModalOpen]);

  // --------------------- Handlers ---------------------
  const onValuesChange = (_changed: Partial<any>, allValues: any) => {
    changing.current = true;
    if (changing.current) setLead(allValues);
  };

  const [phone, setPhone] = useState("");
  const handlePhoneChange = (value: string) => {
    setPhone(value);
    // mantém celular dentro do form também
    form.setFieldValue("celular", value);
    setLead((prev: any) => ({ ...prev, celular: value }));
  };

  // --------------------- Mensagens ---------------------
  useEffect(() => {
    if (isModalOpen) {
      if (success && !successDisplayed) {
        showSuccess("Pessoa cadastrada com sucesso!");
        setSuccessDisplayed(true);

        setTimeout(() => {
          setSuccessDisplayed(false);
          navigate(0);
        }, 1500);
      }

      if (error && !errorDisplayed) {
        showError("Não foi possível cadastrar. Tente novamente!");
        setErrorDisplayed(true);

        setTimeout(() => {
          setErrorDisplayed(false);
          resetError();
        }, 3000);
      }

      if (warning && !warningDisplayed) {
        showWarning("Este telefone já está sendo utilizado!");
        setWarningDisplayed(true);

        setTimeout(() => {
          setWarningDisplayed(false);
          resetWarning();
        }, 3000);
      }
    } else {
      setSuccessDisplayed(false);
      setErrorDisplayed(false);
      setWarningDisplayed(false);
    }
  }, [
    isModalOpen,
    success,
    error,
    warning,
    showSuccess,
    showError,
    showWarning,
    successDisplayed,
    errorDisplayed,
    warningDisplayed,
    resetError,
    resetWarning,
    navigate,
  ]);

  // --------------------- Validação ---------------------
  const validarFormulario = { required: "${label} é necessário!" };

  // --------------------- Submit ---------------------
const handleOk = async () => {
  if (!lead?.celular) return;

  const criado = await cadastrar(lead);

  if (criado?.pessoaId && etiquetasSelecionadas.length > 0) {
    await setEtiquetasPessoaService(criado.pessoaId, etiquetasSelecionadas);
  }
};

  // Resetar form quando abre
  useEffect(() => {
    if (isModalOpen) {
      form.resetFields();
      setPhone("");
      setEtiquetasSelecionadas([]);
      setLead({ nome: "", celular: "", observacoes: "" });
    }
  }, [isModalOpen, form]);

  return (
    <>
      <Modal
        open={isModalOpen}
        onCancel={handleFechar}
        cancelText="Cancelar"
        okText="Cadastrar"
        onOk={handleOk}
        confirmLoading={isLoadingCadastrar}
        width={620}
        okButtonProps={{
          style: {
            backgroundColor: "#003644",
            borderColor: "#003644",
            color: "white",
          },
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: "#f44336",
            borderColor: "#f44336",
            color: "white",
          },
        }}
      >
        <>
          <>{errorContextHolder}</>
          <>{successContextHolder}</>
          <>{warningContextHolder}</>

          <Form
            {...layout}
            form={form}
            initialValues={lead}
            onValuesChange={onValuesChange}
            name="pessoa"
            style={formStyle}
            validateMessages={validarFormulario}
          >
            <Title>Informações</Title>

            <Form.Item name="nome" label="Nome" className="formItems">
              <Input />
            </Form.Item>

            <Form.Item
              name="celular"
              label="Celular"
              className="formItems"
              // não precisa getValueFromEvent aqui
            >
              {/* Wrapper pra garantir que não estoura a modal */}
              <div style={{ width: "100%" }}>
                <PhoneInput
                  country={"br"}
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="Digite seu telefone"
                  inputStyle={{ width: "100%" }}   // ✅ evita estourar
                  containerStyle={{ width: "100%" }} // ✅ evita estourar
                />
              </div>
            </Form.Item>

            <Form.Item
              label="Observações"
              name="observacoes"
              className="formItems"
            >
              <TextArea rows={4} />
            </Form.Item>

            {/* =========================
               ETIQUETAS (opcional)
               - Se você preferir NÃO fazer aqui, pode remover esse bloco sem impacto.
            ========================== */}
            <Form.Item label="Etiquetas" className="formItems">
              <Select
                mode="multiple"
                allowClear
                placeholder="Selecione etiquetas (opcional)"
                value={etiquetasSelecionadas}
                onChange={(ids) => setEtiquetasSelecionadas(ids)}
                optionFilterProp="label"
                options={etiquetasOptions.map((e) => ({
                  value: e.id,
                  label: e.nome,
                }))}
              />
            </Form.Item>
          </Form>
        </>
      </Modal>
    </>
  );
}
